/**
 * BaseDashboard.js
 *
 * Base component with common dashboard logic
 * Handles:
 * - Data fetching from /api/dashboard
 * - Loading/error state handling
 * - Role detection
 * - Data refresh functionality
 * - Caching
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { formatRoleName } from '../../../utils/roleFormatter';
import { useAuth } from '../../auth/hooks/useAuth';
import { getDashboardConfig } from '../../../config/dashboardConfig';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import DepartmentAssignmentBanner from '../components/DepartmentAssignmentBanner';

const DEPARTMENT_SCOPED_ROLES = ['technician', 'coordinator', 'custodian'];
/** Roles with personal device assignment (technician / custodian) */
const USER_ASSIGNMENT_ROLES = ['technician'];

/**
 * Custom hook for dashboard data fetching (no caching - let backend handle it)
 * @param {string} role - User role
 * @param {string} token - Authentication token
 * @returns {Object} { data, loading, error, refetch }
 */
export const useDashboardData = (role, token) => {
  const [data, setData] = useState(null);
  // Only set loading to true if token is available (don't show infinite spinner if auth not ready)
  const [loading, setLoading] = useState(!!token);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch fresh data from API (backend handles caching via Cache-Control headers)
      const response = await axios.get(API_ENDPOINTS.DASHBOARD.GET, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 200) {
        setData(response.data);
      } else {
        setError(response.data?.error || 'Failed to fetch dashboard data');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch dashboard data');
      console.error('Dashboard data fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch on component mount or when token becomes available
  useEffect(() => {
    if (token) {
      fetchData();
    } else {
      // If no token, set loading to false (don't wait for auth context indefinitely)
      setLoading(false);
    }
  }, [token, fetchData]);

  // Refetch function for manual refresh
  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch };
};

/**
 * BaseDashboard Component
 *
 * Provides base structure for all dashboard implementations
 * Handles common rendering logic and state management
 * Automatically detects user role from authentication context
 *
 * Props:
 * - title: Dashboard title (optional, defaults to role-based title)
 * - token: Authentication token for API calls (optional, uses auth context token if not provided)
 * - user: User object (optional, used as fallback for role if AuthContext not yet initialized)
 * - children: Dashboard content (role-specific sections)
 * - loading: Custom loading state (optional)
 * - error: Custom error message (optional)
 * - onRefresh: Callback function to execute on refresh (optional, for child-specific refresh logic)
 * - emptyDepartmentNames: Depts with no personal device assignment (technician passes from assigned-to-me)
 */
const BaseDashboard = ({
  title,
  token: providedToken,
  user: providedUser,
  children,
  loading: isLoading = false,
  error: errorMsg = null,
  onRefresh = null,
  emptyDepartmentNames = [],
}) => {
  // Get authenticated user's role from context
  const authContext = useAuth();
  const { role: contextRole, token: contextToken, user: contextUser, isLoading: authContextLoading } = authContext;

  // Use provided user/role as fallback (for cases where AuthContext hasn't initialized yet)
  // This handles the timing issue where BaseDashboard renders before AuthContext loads from sessionStorage
  const role = contextRole || providedUser?.role || contextUser?.role;
  const user = providedUser || contextUser;

  // Use provided token or fall back to context token
  const token = providedToken || contextToken;

  // Always call the hook with auto-detected role and token (hooks must be called unconditionally)
  const dashboardData = useDashboardData(role, token);
  const { data, loading, error, refetch } = dashboardData;

  const assignedDepartments = user?.departments || [];

  // (a) Personal assignment gaps — technician prop or custodian API (custodian/technician only)
  const noUserAssignmentNames = useMemo(() => {
    if (!USER_ASSIGNMENT_ROLES.includes(role) || assignedDepartments.length === 0) {
      return [];
    }
    if (role === 'technician') {
      return emptyDepartmentNames || [];
    }
    // custodian
    const withAssignments = new Set(data?.data?.departments_with_user_assignments || []);
    return assignedDepartments
      .map((dept) => dept.name)
      .filter((name) => name && !withAssignments.has(name));
  }, [role, assignedDepartments, emptyDepartmentNames, data]);

  // (b) Empty department inventory — all department-scoped roles
  const emptyInventoryNames = useMemo(() => {
    if (!DEPARTMENT_SCOPED_ROLES.includes(role) || assignedDepartments.length === 0) {
      return [];
    }
    const withInventory = new Set(data?.data?.departments_with_inventory || []);
    // If API hasn't provided inventory coverage yet, don't mark everything empty
    if (!data?.data || !Object.prototype.hasOwnProperty.call(data.data, 'departments_with_inventory')) {
      return [];
    }
    return assignedDepartments
      .map((dept) => dept.name)
      .filter((name) => name && !withInventory.has(name));
  }, [role, assignedDepartments, data]);

  const showUserAssignment = USER_ASSIGNMENT_ROLES.includes(role);

  // Create enhanced refetch that includes child-specific refresh logic
  const enhancedRefetch = useCallback(() => {
    // First refetch main dashboard data
    refetch();
    // Then call child-specific refresh if provided (e.g., security metrics for AdminDashboard)
    if (onRefresh) {
      onRefresh();
    }
  }, [refetch, onRefresh]);

  // Guard: Wait for auth context to initialize and data to be fetched
  // Check authContextLoading for AuthContext initialization, then check for valid role/token and dashboard data
  if (authContextLoading || !role || !token || isLoading || loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Error state (use prop first, then hook state)
  if (errorMsg || error) {
    return (
      <div className="dashboard-error">
        <div className="error-icon">⚠️</div>
        <h3>Failed to Load Dashboard</h3>
        <p>{errorMsg || error}</p>
        <button onClick={refetch} className="btn-retry">
          Retry
        </button>
      </div>
    );
  }

  // Get dashboard descriptions based on role
  const getDescription = () => {
    switch(role) {
      case 'admin':
        return 'Manage your location, track device portfolio, monitor maintenance compliance, and review financial metrics across your responsibility areas.';
      case 'superadmin':
        return 'Enterprise-wide oversight of all devices, locations, departments, and financial performance with comprehensive analytics and reporting.';
      case 'coordinator':
        return 'Manage your department\'s device inventory, maintenance schedules, complaints, and ensure compliance with regulatory requirements.';
      case 'custodian':
        return 'Monitor your assigned devices, track maintenance activities, view scheduled maintenance, and ensure device availability.';
      case 'manager':
        return 'Oversee all departments within your location, manage location-wide budgets, monitor device performance, track team workload, and ensure comprehensive compliance across all operations.';
      case 'technician':
        return 'View your assigned devices, maintenance tasks, and active complaints to keep all equipment in optimal condition.';
      default:
        return 'View and manage your device portfolio, maintenance schedules, complaints, and compliance status.';
    }
  };

  // Get dashboard config for role to customize title and icon
  const dashboardConfig = getDashboardConfig(role);
  const configTitle = title || dashboardConfig.title || `${formatRoleName(role)} Dashboard`;
  const configIcon = dashboardConfig.icon || '🏠';
  const showDepartmentBanner =
    DEPARTMENT_SCOPED_ROLES.includes(role) && assignedDepartments.length >= 1;

  // Render dashboard with data
  return (
    <div className="dashboard-container">
      <div className="dashboard-description-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h1>{configIcon} {configTitle}</h1>
          <button
            onClick={enhancedRefetch}
            className="btn-refresh"
            title="Refresh dashboard"
            aria-label="Refresh dashboard"
            style={{ padding: '0.5rem', fontSize: '1.2rem', border: 'none', background: 'none', cursor: 'pointer', lineHeight: 1 }}
          >
            🔄
          </button>
        </div>
        <p>{getDescription()}</p>
      </div>

      <div className="dashboard-content">
        {showDepartmentBanner && (
          <DepartmentAssignmentBanner
            departments={assignedDepartments}
            noUserAssignmentNames={noUserAssignmentNames}
            emptyInventoryNames={emptyInventoryNames}
            showUserAssignment={showUserAssignment}
          />
        )}
        {typeof children === 'function' ? children(data, enhancedRefetch) : children}
      </div>
    </div>
  );
};

export default BaseDashboard;
